import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, TextField, Grid,
  Divider, CircularProgress
} from "@mui/material";
import { db } from "../firebase";
import { collection, query, where, onSnapshot, getDoc, doc } from "firebase/firestore";

function formatEuro(valore) {
  return `€ ${Number(valore).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AnalisiCostiRicavi({ eventoId, form, handleChange }) {
  const [costiEvento, setCostiEvento] = useState([]);
  const [tariffe, setTariffe] = useState({ tariffeOrariaAlgores: 40, tariffeOrariaCollaboratori: 15 });
  const [loading, setLoading] = useState(true);

  // Carica tariffe da Firestore
  useEffect(() => {
    getDoc(doc(db, "impostazioni", "tariffe")).then((snap) => {
      if (snap.exists()) setTariffe(snap.data());
      setLoading(false);
    });
  }, []);

  // Carica costi evento
  useEffect(() => {
    if (!eventoId) return;
    const q = query(collection(db, "costi"), where("eventoId", "==", eventoId));
    const unsub = onSnapshot(q, (snap) => {
      setCostiEvento(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [eventoId]);

  // Calcoli
  const importoVendita = parseFloat(form.importoVendita) || 0;
  const oreAlgores = parseFloat(form.oreAlgores) || 0;
  const oreCollaboratori = parseFloat(form.oreCollaboratori) || 0;
  const costiAggiuntivi = parseFloat(form.costiAggiuntivi) || 0;

  const totaleCostiEvento = costiEvento.reduce((acc, c) => acc + (parseFloat(c.importo) || 0), 0);
  const costoAlgores = oreAlgores * tariffe.tariffeOrariaAlgores;
  const costoCollaboratori = oreCollaboratori * tariffe.tariffeOrariaCollaboratori;
  const totaleCosti = totaleCostiEvento + costiAggiuntivi + costoAlgores + costoCollaboratori;
  const ricavi = importoVendita - totaleCosti;

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
        Analisi costi / ricavi
      </Typography>

      {/* RICAVI */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: "#E6F1FB", borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Importo di vendita</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {formatEuro(importoVendita)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Margine</Typography>
            <Typography variant="h5" fontWeight="bold"
              color={ricavi >= 0 ? "success.main" : "error.main"}>
              {formatEuro(ricavi)}
              <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                ({importoVendita > 0 ? Math.round(ricavi / importoVendita * 100) : 0}%)
              </Typography>
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* DETTAGLIO COSTI */}
      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
        sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: 11 }}>
        Dettaglio costi
      </Typography>

      <Grid container spacing={2}>
        {/* Costi evento da CostiEvento */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Costi evento
            </Typography>
            <Typography variant="h6" fontWeight="bold">{formatEuro(totaleCostiEvento)}</Typography>
            {costiEvento.length > 0 && (
              <Box mt={1}>
                {costiEvento.map((c) => (
                  <Box key={c.id} display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{c.categoria || c.descrizione || "—"}</Typography>
                    <Typography variant="caption">{formatEuro(parseFloat(c.importo) || 0)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Costi aggiuntivi manuali */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Costi aggiuntivi
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Importo (€)"
              value={form.costiAggiuntivi || ""}
              onChange={(e) => handleChange("costiAggiuntivi", e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Paper>
        </Grid>

        {/* Costo personale Algores */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Costo personale Algores
            </Typography>
            <Typography variant="h6" fontWeight="bold">{formatEuro(costoAlgores)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {oreAlgores}h × €{tariffe.tariffeOrariaAlgores}/h
            </Typography>
          </Paper>
        </Grid>

        {/* Costo collaboratori */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Costo collaboratori
            </Typography>
            <Typography variant="h6" fontWeight="bold">{formatEuro(costoCollaboratori)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {oreCollaboratori}h × €{tariffe.tariffeOrariaCollaboratori}/h
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* TOTALE COSTI */}
      <Box display="flex" justifyContent="space-between" alignItems="center"
        sx={{ backgroundColor: "#FDECEA", p: 2, borderRadius: 2 }}>
        <Typography fontWeight="bold">Totale costi</Typography>
        <Typography variant="h6" fontWeight="bold" color="error.main">
          {formatEuro(totaleCosti)}
        </Typography>
      </Box>
    </Box>
  );
}